import React, { useMemo, useState } from 'react';
import type { ActivityViewProps } from '../../core/types/activity.types';
import type { CompletarPayload, CompletarRuntime } from '../../core/types/payloads/completar.types';
import type { DesarrolloPayload, DesarrolloRuntime } from '../../core/types/payloads/desarrollo.types';
import type { VinculaPayload, VinculaRuntime } from '../../core/types/payloads/vincula.types';
import type { AgrupaPayload, AgrupaRuntime } from '../../core/types/payloads/agrupa.types';
import { GeometryCanvas } from '../../core/math/GeometryCanvas';

function dispatchSubmit(dispatch: ActivityViewProps['dispatch'], action: string, data?: unknown) {
  void dispatch({ type: action, payload: data });
}

// ── Completar ──
function CompletarStudent({ payload, runtime, dispatch, participantId }: ActivityViewProps<'completar'>) {
  const rt = runtime as CompletarRuntime;
  const answers = participantId ? rt.responses[participantId] ?? {} : {};
  const finalized = participantId ? rt.finalized[participantId] : false;
  const parts = payload.text.split(/(\[[^\]]+\])/g);

  const chips = useMemo(() => {
    if (payload.inputMode !== 'drag') return [];
    if (payload.chipPool?.length) return payload.chipPool;
    return [...payload.blanks.map((b) => b.answer), ...(payload.distractors ?? [])];
  }, [payload]);

  return (
    <div className="eval-block eval-block--completar">
      <h2 className="eval-block__title">{payload.title}</h2>
      {payload.geometry?.shapeType && (
        <div className="eval-block__geometry">
          <GeometryCanvas geometry={payload.geometry} width={360} height={240} />
        </div>
      )}
      {payload.context && <p className="eval-block__context">{payload.context}</p>}
      <p className="eval-block__body">
        {parts.map((part, i) => {
          const m = part.match(/^\[(.+)\]$/);
          if (!m) return <span key={i}>{part}</span>;
          const blank = payload.blanks.find((b) => b.answer === m[1]!.trim());
          const id = blank?.id ?? `b${i}`;
          return (
            <span key={i} className="eval-cloze-blank">
              {payload.inputMode === 'keyboard' ? (
                <input
                  className={`eval-cloze-input${answers[id] ? ' eval-cloze-input--filled' : ''}`}
                  disabled={finalized}
                  value={answers[id] ?? ''}
                  onChange={(e) => dispatchSubmit(dispatch, 'FILL', { blankId: id, value: e.target.value })}
                />
              ) : (
                <span className={`eval-cloze-slot${answers[id] ? ' eval-cloze-slot--filled' : ''}`}>{answers[id] || '···'}</span>
              )}
            </span>
          );
        })}
      </p>
      {payload.inputMode === 'drag' && !finalized && (
        <div className="eval-cloze-chips">
          {chips.map((chip) => (
            <button
              key={chip}
              type="button"
              className="eval-cloze-chip"
              onClick={() => {
                const empty = payload.blanks.find((b) => !answers[b.id]);
                if (empty) dispatchSubmit(dispatch, 'FILL', { blankId: empty.id, value: chip });
              }}
            >
              {chip}
            </button>
          ))}
        </div>
      )}
      {!finalized && (
        <button type="button" className="sala-btn-hero mt-4 text-xs" onClick={() => dispatchSubmit(dispatch, 'SUBMIT')}>
          Enviar respuesta
        </button>
      )}
    </div>
  );
}

function CompletarHost({ payload, runtime }: ActivityViewProps<'completar'>) {
  const rt = runtime as CompletarRuntime;
  return (
    <div className="eval-block eval-block--host">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__stats">
        Enviados: {rt.aggregates.submitted} · Aciertos totales: {rt.aggregates.correct}
      </p>
    </div>
  );
}

export const CompletarStudentView = CompletarStudent;
export const CompletarHostView = CompletarHost;
export const CompletarProjectorView = CompletarHost;

// ── Desarrollo ──
function DesarrolloStudent({ payload, runtime, dispatch, participantId }: ActivityViewProps<'desarrollo'>) {
  const rt = runtime as DesarrolloRuntime;
  const text = participantId ? rt.responses[participantId]?.text ?? '' : '';
  const submitted = Boolean(rt.responses[participantId]?.submittedAt);

  return (
    <div className="eval-block eval-block--desarrollo">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__prompt">{payload.prompt}</p>
      <textarea
        className="eval-open-text"
        disabled={submitted}
        value={text}
        maxLength={payload.maxChars}
        onChange={(e) => dispatchSubmit(dispatch, 'DRAFT', { text: e.target.value })}
        placeholder="Redacta tu respuesta…"
      />
      <p className="eval-char-count">
        {text.length} / {payload.maxChars} caracteres
      </p>
      {!submitted && (
        <button
          type="button"
          className="sala-btn-hero mt-3 text-xs"
          disabled={text.length < (payload.minChars ?? 0)}
          onClick={() => dispatchSubmit(dispatch, 'SUBMIT')}
        >
          Enviar desarrollo
        </button>
      )}
    </div>
  );
}

function DesarrolloHost({ payload, runtime }: ActivityViewProps<'desarrollo'>) {
  const rt = runtime as DesarrolloRuntime;
  return (
    <div className="eval-block eval-block--host">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__stats">Entregas: {rt.aggregates.submitted}</p>
      <ul className="mt-3 space-y-1 text-xs font-bold uppercase tracking-wide text-[var(--sala-stark-muted,#1e293b)]">
        {payload.rubric.map((r) => (
          <li key={r}>• {r}</li>
        ))}
      </ul>
    </div>
  );
}

export const DesarrolloStudentView = DesarrolloStudent;
export const DesarrolloHostView = DesarrolloHost;
export const DesarrolloProjectorView = DesarrolloHost;

// ── Vincula ──
function VinculaStudent({ payload, runtime, dispatch, participantId }: ActivityViewProps<'vincula'>) {
  const rt = runtime as VinculaRuntime;
  const links = participantId ? rt.links[participantId] ?? {} : {};
  const finalized = participantId ? rt.finalized[participantId] : false;
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);
  const pairById = useMemo(() => new Map(payload.pairs.map((p) => [p.id, p])), [payload.pairs]);
  const rightItems = useMemo(() => {
    const order = payload.rightColumnOrder?.length
      ? payload.rightColumnOrder
      : payload.pairs.map((p) => p.id);
    return order.map((id) => pairById.get(id)).filter(Boolean) as typeof payload.pairs;
  }, [payload.pairs, payload.rightColumnOrder, pairById]);

  return (
    <div className="eval-block eval-block--vincula">
      <h2 className="eval-block__title">{payload.title}</h2>
      <div className="eval-vincula-grid">
        <div className="eval-vincula-col">
          {payload.pairs.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={finalized}
              className={`eval-vincula-card${selectedLeft === p.id ? ' eval-vincula-card--active' : ''}${links[p.id] ? ' eval-vincula-card--linked' : ''}`}
              onClick={() => setSelectedLeft(p.id)}
            >
              {p.ladoA}
            </button>
          ))}
        </div>
        <div className="eval-vincula-col">
          {rightItems.map((p) => (
            <button
              key={`r-${p.id}`}
              type="button"
              disabled={finalized || !selectedLeft}
              className="eval-vincula-card eval-vincula-card--right"
              onClick={() => {
                if (!selectedLeft) return;
                dispatchSubmit(dispatch, 'LINK', { leftId: selectedLeft, rightId: p.id });
                setSelectedLeft(null);
              }}
            >
              {p.ladoB}
            </button>
          ))}
        </div>
      </div>
      {!finalized && (
        <button type="button" className="sala-btn-hero mt-4 text-xs" onClick={() => dispatchSubmit(dispatch, 'SUBMIT')}>
          Confirmar vínculos
        </button>
      )}
    </div>
  );
}

function VinculaHost({ payload, runtime }: ActivityViewProps<'vincula'>) {
  const rt = runtime as VinculaRuntime;
  return (
    <div className="eval-block eval-block--host">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__stats">
        {rt.aggregates.submitted} envíos · {rt.aggregates.correct} aciertos
      </p>
    </div>
  );
}

export const VinculaStudentView = VinculaStudent;
export const VinculaHostView = VinculaHost;
export const VinculaProjectorView = VinculaHost;

// ── Agrupa ──
function AgrupaStudent({ payload, runtime, dispatch, participantId }: ActivityViewProps<'agrupa'>) {
  const rt = runtime as AgrupaRuntime;
  const placements = participantId ? rt.placements[participantId] ?? {} : {};
  const finalized = participantId ? rt.finalized[participantId] : false;
  const unplaced = payload.items.filter((it) => !placements[it.id]);

  return (
    <div className="eval-block eval-block--agrupa">
      <h2 className="eval-block__title">{payload.title}</h2>
      <div className="eval-agrupa-columns">
        {payload.categories.map((cat) => (
          <div key={cat.id} className="eval-agrupa-col" style={{ background: cat.color ?? '#f8fafc' }}>
            <p className="eval-agrupa-col__label">{cat.label}</p>
            {payload.items
              .filter((it) => placements[it.id] === cat.id)
              .map((it) => (
                <span key={it.id} className="eval-agrupa-item">
                  {it.text}
                </span>
              ))}
          </div>
        ))}
      </div>
      {!finalized && unplaced.length > 0 && (
        <div className="eval-agrupa-tray">
          {unplaced.map((it) => (
            <div key={it.id} className="eval-agrupa-tray-item">
              <span>{it.text}</span>
              <select
                className="eval-agrupa-select"
                value={placements[it.id] ?? ''}
                onChange={(e) =>
                  dispatchSubmit(dispatch, 'PLACE', { itemId: it.id, categoryId: e.target.value })
                }
              >
                <option value="">Clasificar…</option>
                {payload.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
      {!finalized && (
        <button type="button" className="sala-btn-hero mt-4 text-xs" onClick={() => dispatchSubmit(dispatch, 'SUBMIT')}>
          Enviar clasificación
        </button>
      )}
    </div>
  );
}

function AgrupaHost({ payload, runtime }: ActivityViewProps<'agrupa'>) {
  const rt = runtime as AgrupaRuntime;
  return (
    <div className="eval-block eval-block--host">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__stats">
        {rt.aggregates.submitted} envíos · {rt.aggregates.correct} aciertos
      </p>
    </div>
  );
}

export const AgrupaStudentView = AgrupaStudent;
export const AgrupaHostView = AgrupaHost;
export const AgrupaProjectorView = AgrupaHost;
